'use client';

import { CheckCircle, Warning } from '@phosphor-icons/react/ssr';
import { type ComponentProps, type ReactNode, useId } from 'react';

import { tv } from '../../lib/tv';
import { cn } from '../../lib/utils';
import { Progress } from '../ui/progress';
import { FeedbackKeyframes } from './keyframes';
import { Link } from './link';

export type ProgressTone = 'brand' | 'success' | 'danger';

/**
 * Figma Progress Bar (80:63) and Progress Bar Indeterminate (80:68): a
 * bg/emphasis track, Small 4px (radius 2) or Medium 8px (radius/xs 4), with a
 * Fill in brand/strong, status/success/solid or status/danger/solid. The
 * indeterminate Runner is 35% wide, brand/strong, 1.4s ease-in-out loop;
 * under reduced motion it stays at 30% and pulses its opacity instead.
 */
const bar = tv({
  slots: { root: 'w-full', fill: '' },
  variants: {
    size: {
      sm: { root: 'h-1 rounded-[2px]' },
      md: { root: 'h-2 rounded-xs' },
    },
    tone: {
      brand: { fill: 'bg-brand-strong' },
      success: { fill: 'bg-status-success-solid' },
      danger: { fill: 'bg-status-danger-solid' },
    },
    indeterminate: {
      true: {
        fill: [
          'w-[35%]',
          'motion-safe:animate-[rp-progress-runner_1.4s_ease-in-out_infinite]',
          'motion-reduce:ml-[30%] motion-reduce:animate-[rp-progress-pulse_1.4s_ease-in-out_infinite]',
        ],
      },
    },
  },
  defaultVariants: { size: 'md', tone: 'brand', indeterminate: false },
});

export type ProgressBarProps = Omit<
  ComponentProps<typeof Progress>,
  'value' | 'indicatorClassName'
> & {
  /** 0–`max`. `null` / omitted = indeterminate (Progress Bar Indeterminate). */
  value?: number | null;
  /** Figma "Size": Small 4px (in rows and cards) · Medium 8px (Progress Field). */
  size?: 'sm' | 'md';
  /** Figma "Tone": Brand while running, Success on completion, Danger when the job fails. */
  tone?: ProgressTone;
};

/**
 * Determinate or indeterminate progress bar (Figma: ↳ Progress ↳ Progress
 * Bar / Progress Bar Indeterminate). A Radix `progressbar`: give it a name
 * (`aria-label` / `aria-labelledby`) and always show the value as text next
 * to it - the bar alone is not accessible. Fills its container (Figma draws
 * it at 320). Switch from indeterminate to a value as soon as one is known.
 */
export function ProgressBar({
  value = null,
  size,
  tone,
  className,
  ...props
}: ProgressBarProps) {
  const indeterminate = value == null;
  const styles = bar({ size, tone, indeterminate });
  return (
    <>
      {indeterminate && <FeedbackKeyframes />}
      <Progress
        value={value}
        data-size={size ?? 'md'}
        data-tone={tone ?? 'brand'}
        className={styles.root({ className })}
        indicatorClassName={styles.fill()}
        {...props}
      />
    </>
  );
}

/**
 * Figma Progress Circle (80:109): a ring of bg/emphasis with the value arc in
 * brand/strong (status/success/solid at 100%), from 12 o'clock clockwise,
 * flat ends. Ring width = radius × (1 − inner radius ratio): Small 20 (0.62),
 * Medium 40 (0.78), Large 64 (0.82, with the Label/Small percentage).
 */
export const CIRCLE_SIZE = {
  sm: { box: 20, inner: 0.62 },
  md: { box: 40, inner: 0.78 },
  lg: { box: 64, inner: 0.82 },
} as const;

export type ProgressCircleProps = Omit<ComponentProps<'div'>, 'children'> & {
  /** 0–`max`. */
  value: number;
  max?: number;
  /** Figma "Size": Small 20 inline · Medium 40 in lists · Large 64 with percentage. */
  size?: 'sm' | 'md' | 'lg';
  /** Show the percentage in the middle. Defaults to on for Large only (Figma). */
  showValue?: boolean;
};

/**
 * Compact determinate progress for cards, nodes and upload chips (Figma:
 * ↳ Progress ↳ Progress Circle). `role="progressbar"` with `aria-value*`;
 * name it with `aria-label`. Completes in Success green. For unknown
 * durations use a spinner, not this.
 */
export function ProgressCircle({
  value,
  max = 100,
  size = 'md',
  showValue = size === 'lg',
  className,
  ...props
}: ProgressCircleProps) {
  const clamped = Math.min(max, Math.max(0, value));
  const percent = Math.round((clamped / max) * 100);
  const complete = clamped >= max;
  const { box, inner } = CIRCLE_SIZE[size];
  const stroke = (box / 2) * (1 - inner);
  const r = box / 2 - stroke / 2;
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={clamped}
      aria-valuetext={`${percent}%`}
      data-slot="progress-circle"
      data-size={size}
      data-state={complete ? 'complete' : 'loading'}
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center',
        className,
      )}
      style={{ width: box, height: box }}
      {...props}
    >
      <svg
        aria-hidden
        width={box}
        height={box}
        viewBox={`0 0 ${box} ${box}`}
        className="absolute inset-0 -rotate-90"
      >
        <circle
          data-slot="progress-circle-track"
          cx={box / 2}
          cy={box / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          className="stroke-bg-emphasis"
        />
        <circle
          data-slot="progress-circle-value"
          cx={box / 2}
          cy={box / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          pathLength={100}
          strokeDasharray={`${(clamped / max) * 100} 100`}
          className={cn(
            'transition-[stroke-dasharray] duration-300 ease-out motion-reduce:transition-none',
            complete ? 'stroke-status-success-solid' : 'stroke-brand-strong',
          )}
        />
      </svg>
      {showValue && (
        <span
          aria-hidden
          data-slot="progress-circle-value-text"
          className="text-label-sm text-text-primary relative"
        >
          {percent}%
        </span>
      )}
    </div>
  );
}

/** Figma "Status" of the Progress Field. */
export type ProgressFieldStatus = 'active' | 'complete' | 'error';

const field = tv({
  slots: {
    root: 'flex w-full flex-col gap-2',
    top: 'flex items-center gap-2',
    label: 'text-label-md text-text-primary min-w-0 flex-1 truncate',
    value: 'text-data-md shrink-0 tabular-nums',
    status: 'flex items-center gap-1.5',
    statusIcon: 'size-3.5 shrink-0',
    caption: 'text-caption min-w-0 flex-1',
  },
  variants: {
    status: {
      active: { value: 'text-text-secondary', caption: 'text-text-tertiary' },
      complete: {
        value: 'text-status-success-fg',
        statusIcon: 'text-status-success-fg',
        caption: 'text-text-tertiary',
      },
      error: {
        value: 'text-status-danger-fg',
        statusIcon: 'text-status-danger-fg',
        caption: 'text-status-danger-fg',
      },
    },
  },
  defaultVariants: { status: 'active' },
});

const FIELD_TONE: Record<ProgressFieldStatus, ProgressTone> = {
  active: 'brand',
  complete: 'success',
  error: 'danger',
};

export type ProgressFieldProps = Omit<ComponentProps<'div'>, 'children'> & {
  /** Figma "Label": what is running ("Rendering variations"). */
  label: ReactNode;
  /** 0–`max`. Complete always draws a full bar; Error keeps the bar where it stopped. */
  value: number;
  max?: number;
  /** Figma "Status": Active → Complete, or Error. */
  status?: ProgressFieldStatus;
  /** The value text on the right. Defaults to the percentage ("58%"). */
  valueText?: ReactNode;
  /**
   * Status line: count and time left while active ("7 of 12 variations ·
   * about 2 min left"), duration when complete, the reason on error.
   */
  caption?: ReactNode;
  /** Error: shows the Retry link. */
  onRetry?: () => void;
  retryLabel?: string;
};

/**
 * Labelled progress for long jobs - renders, uploads (Figma: ↳ Progress ↳
 * Progress Field). Label + percentage + Medium bar + status line. The bar
 * is named by the label and described by the status line; Complete and
 * Error are announced (the status line is a polite live region then - not
 * while active, so each tick isn't read out).
 */
export function ProgressField({
  label,
  value,
  max = 100,
  status = 'active',
  valueText,
  caption,
  onRetry,
  retryLabel = 'Retry',
  className,
  ...props
}: ProgressFieldProps) {
  const styles = field({ status });
  const labelId = useId();
  const captionId = useId();
  const shown = status === 'complete' ? max : Math.min(max, Math.max(0, value));
  const percent = Math.round((shown / max) * 100);
  const StatusIcon =
    status === 'complete' ? CheckCircle : status === 'error' ? Warning : null;
  return (
    <div
      data-slot="progress-field"
      data-status={status}
      className={styles.root({ className })}
      {...props}
    >
      <div className={styles.top()}>
        <span id={labelId} data-slot="progress-field-label" className={styles.label()}>
          {label}
        </span>
        <span data-slot="progress-field-value" className={styles.value()}>
          {valueText ?? `${percent}%`}
        </span>
      </div>
      <ProgressBar
        value={shown}
        max={max}
        size="md"
        tone={FIELD_TONE[status]}
        aria-labelledby={labelId}
        aria-describedby={caption ? captionId : undefined}
        data-slot="progress-field-bar"
      />
      <div
        data-slot="progress-field-status"
        className={styles.status()}
        aria-live={status === 'active' ? 'off' : 'polite'}
      >
        {StatusIcon && <StatusIcon aria-hidden className={styles.statusIcon()} />}
        <span id={captionId} className={styles.caption()}>
          {caption}
        </span>
        {status === 'error' && onRetry && (
          <Link asChild size="sm">
            <button type="button" onClick={onRetry}>
              {retryLabel}
            </button>
          </Link>
        )}
      </div>
    </div>
  );
}
